<template>
  <VSpinner v-if="!product" class="offset-top-16 offset-bottom-17" />
  <article v-else>
    <div id="PRODUCT_DETAILS" class="container-fixed">
      <header class="offset-bottom-2">
        <h3 class="h1" v-html="product.texts.productName"><!-- #21 --></h3>
      </header>

      <div class="row">
        <div class="col-l-6 col-s-12">
          <div v-if="product.images.productLogoDetailsResourcePath" class="s-center-l-left offset-s-bottom-1">
            <!-- #24: productLogoDetails -->
            <img
              :src="'delegates/product/content/' + product.images.productLogoDetailsResourcePath"
              :alt="product.images.productLogoDetailsAlt"
              class="product-image"
            />
          </div>
        </div>

        <div class="col-l-6 col-s-12">
          <div v-if="product.images.providerLogoDetailsResourcePath" class="s-center-l-right offset-s-top-1">
            <!-- #28: providerLogoDetails -->
            <img
              :src="'delegates/product/content/' + product.images.providerLogoDetailsResourcePath"
              :alt="product.images.providerLogoDetailsAlt"
              class="product-image"
            />
          </div>
        </div>
      </div>

      <VSection cluster label="substore.description">
        <p v-html="product.texts.productDescriptionDetails"><!-- #25 --></p>

        <p>
          <strong>
            <!-- #26 -->
            {{ $t('substore.provider') }}:
            <!-- #27 -->
            <span v-html="product.texts.providerName"></span>
          </strong>
        </p>
      </VSection>

      <VSection v-if="product.texts.productRequirementsDetails" cluster label="substore.requirements">
        <p v-html="product.texts.productRequirementsDetails"><!-- #30 --></p>
      </VSection>

      <VSection v-if="isGallery" cluster label="substore.gallery">
        <p>
          <Carousel
            :per-page-custom="[
              [1, 1],
              [264, 2],
              [408, 3],
              [552, 4],
              [696, 5],
              [840, 6],
              [984, 7],
              [1128, 8],
            ]"
            pagination-color="#ededed"
            pagination-active-color="#e20074"
          >
            <Slide v-for="(image, index) in product.gallery" :key="'image' + index">
              <!-- #32 -->
              <a href="#" :title="image.thumbnailGallery.alt" @click.prevent="imageIndex = index">
                <img :src="'delegates/product/content/' + image.thumbnailGallery.resourcePath" alt="" style="max-width: 120px; max-height: 120px;" />
              </a>
            </Slide>
          </Carousel>

          <VGallery :images="images" :index="imageIndex" @close="imageIndex = null"></VGallery>
        </p>
      </VSection>

      <VSection v-if="isProductGroup" cluster label="substore.package">
        <p v-if="product.texts.productGroupUpperDescriptionDetails" v-html="product.texts.productGroupUpperDescriptionDetails"><!-- #239 --></p>

        <component :is="packageHook" v-on="packageListeners">
          <div class="form-input-set">
            <label for="selectProduct" v-html="product.texts.productGroupSelectionLabelDetails || $t('substore.variant')"><!-- #240 --></label>
            <select id="selectProduct" class="form-select" @change="overrideWithProductGroup($event.target.value)">
              <option
                v-for="(p, index) in product.productGroupMembers"
                :key="index"
                :value="index"
                :selected="p.subscriptionTemplateId === subscriptionTemplateId"
                v-html="p.productName"
              >
                <!-- #241 -->
              </option>
            </select>
          </div>
        </component>

        <p v-if="product.texts.productGroupLowerDescriptionDetails" v-html="product.texts.productGroupLowerDescriptionDetails"><!-- #242 --></p>
      </VSection>
    </div>

    <div class="container-fluid grayed">
      <div class="container-fixed">
        <VSection cluster label="substore.price_and_contract_information">
          <component :is="priceHook" v-bind="priceBindings" v-on="priceListeners">
            <VPriceDetails v-bind="product" />
          </component>
        </VSection>
      </div>
    </div>

    <div class="container-fixed">
      <div v-if="isProductChangeAllowedOne || isProductChangeAllowedMultiple" class="row">
        <div class="col-l-12">
          <VSection cluster>
            <p
              v-if="isProductChangeAllowedOne"
              v-html="$t('substore.productdetails_allowed_product_change', { productName: product.allowedProductChangeFrom[0].productName })"
            />
            <p v-else v-html="$t('substore.productdetails_allowed_product_change_multiple')" />
          </VSection>
        </div>
      </div>

      <div class="row">
        <section class="offset-top-2 pull-right offset-right-1 button-bar">
          <!-- #328 -->
          <VButton
            v-if="!isDeeplink"
            class="offset-left-1 offset-bottom-1 btn-clean"
            :to="{ name: PRODUCT_CATALOGUE }"
            label="substore.productdetails_link_back"
          />

          <VButton
            v-if="isProductChangeAllowedOne"
            class="offset-left-1 offset-bottom-1 btn-default"
            :to="{
              name: STOCK_PRODUCT_CHANGE_SUMMARY,
              params: {
                newSubscriptionTemplateId: selectedSubscriptionTemplateId,
                subscriptionTemplateId: product.allowedProductChangeFrom[0].subscriptionTemplateId,
                uniqueSubscriptionId: product.allowedProductChangeFrom[0].uniqueSubscriptionId,
              },
            }"
            label="substore.productdetails_btn_change"
          />
          <VButton
            v-else-if="isProductChangeAllowedMultiple"
            class="offset-left-1 offset-bottom-1 btn-default"
            :to="{ name: STOCK }"
            label="substore.productdetails_btn_stock"
          />

          <!-- #41 -->
          <VButton
            v-if="product.bookingAllowed"
            class="offset-left-1 offset-bottom-1 btn-default btn-brand"
            :disabled="isNextDisabled"
            :label="btnNextLabel"
            @click="onNext"
          />
          <span v-else class="offset-left-1 offset-bottom-1" v-html="btnNextLabel"></span>
        </section>
      </div>
    </div>

    <div class="container-fixed offset-top-6">
      <VFootnotes :footnotes="footnotes" />
    </div>
  </article>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { Carousel, Slide } from '@/components/UI/Carousel';
import Proxy from '@/proxies/SubstoreProductDetailsProxy';
import Transformer from '@/transformers/ProductDetailsTransformer';
import Footnotes from '@/utils/Footnotes';
import { productDetailsService } from '@/utils/hookServices';
import TitleMixin from '@/mixins/title';
import VSpinner from '@/components/Spinner';
import VButton from '@/components/Button';
import VTransparent from '@/components/Transparent';
import VSection from '@/components/Section';
import emitter from '@/plugins/emitter';
import { PRODUCT_CATALOGUE, PRODUCT_DETAILS, PRODUCT_ORDER_SELECTION, STOCK, STOCK_PRODUCT_CHANGE_SUMMARY } from '@route-types';

export default {
  name: 'ProductDetails',

  components: {
    VSpinner,
    VButton,
    VSection,
    Carousel,
    Slide,
    VFootnotes: () => import('@/components/UI/Footnotes'),
    VPriceDetails: () => import('@/components/Substore/PriceDetails'),
    VGallery: () => import('vue-gallery'),
  },

  mixins: [TitleMixin],

  props: {
    subscriptionTemplateId: {
      type: String,
      required: true,
    },

    originalSubscriptionTemplateId: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      PRODUCT_CATALOGUE,
      STOCK,
      STOCK_PRODUCT_CHANGE_SUMMARY,
      selectedSubscriptionTemplateId: this.subscriptionTemplateId,

      product: null,
      footnotes: null,
      isProductGroup: false,
      images: [],
      imageIndex: null,

      isNextDisabled: false,
      hookService: null,
      packageHook: VTransparent,
      packageListeners: {},

      priceHook: VTransparent,
      priceBindings: {},
      priceListeners: {},
    };
  },

  computed: {
    ...mapState('system', ['isDeeplink', 'deeplinkSubscriptionTemplateId']),

    ...mapGetters('system', ['hasAccessStockOverview']),

    isProductChangeAllowedOne() {
      return this.product.allowedProductChangeFrom?.length === 1;
    },

    isProductChangeAllowedMultiple() {
      return this.product.allowedProductChangeFrom?.length > 1 && this.hasAccessStockOverview;
    },

    isGallery() {
      return this.product.gallery.length > 0;
    },

    btnNextLabel() {
      if (this.product.bookingAllowed) {
        if (this.product.reservedInstances > 0) {
          return this.$t('substore.productdetails_btn_order_complete');
        }
        return this.$t('substore.productdetails_btn_order');
      } else if (this.product.canNotSucceed) {
        return this.$t('substore.msg_order_currently_not_possible');
      } else if (this.product.reservedInstances > 0) {
        return this.$t('substore.productdetails_btn_order_reserved');
      } else if (this.product.excludedBy.length > 0) {
        return this.$t('substore.productdetails_btn_order_excludedby', { excludedBy: this.product.excludedBy[0].productName });
      } else if (this.product.currentInstances >= this.product.maxInstances) {
        return this.$t('substore.productdetails_btn_order_alreadybooked');
      }

      return this.$t('substore.productdetails_btn_order_notallowed');
    },

    baseSubscriptionTemplateId() {
      return this.originalSubscriptionTemplateId || this.subscriptionTemplateId;
    },

    optionalSubscriptionTemplateId() {
      return this.baseSubscriptionTemplateId === this.selectedSubscriptionTemplateId ? null : this.baseSubscriptionTemplateId;
    },
  },

  async created() {
    if (this.isDeeplink && this.deeplinkSubscriptionTemplateId && !this.subscriptionTemplateId) {
      this.$router.replace({
        name: PRODUCT_DETAILS,
        params: { subscriptionTemplateId: this.deeplinkSubscriptionTemplateId },
      });
      return;
    }
    if (this.isDeeplink && this.subscriptionTemplateId) {
      this.$store.dispatch('system/resetDeeplink');
    }

    const response = await new Proxy().find(this.baseSubscriptionTemplateId);

    if (response.head.fault !== null) throw new Error(response.head.fault);

    this.footnotes = new Footnotes();
    this.product = Transformer.fetch(response.data.product, this.footnotes);
    emitter.emit('setBannerPicture', this.product.images.headerImageDetailsResourcePath);
    emitter.emit('setBannerHeadline', this.product.texts.headerTitleDetails);
    emitter.emit('setBannerSubheadline', this.product.texts.headerSubTitleDetails);

    if (this.isGallery) {
      this.images = this.product.gallery.map(image => `delegates/product/content/${image.imageGallery.resourcePath}`);
    }

    this.$webtrekk.viewPageWithPayload('produktdetails', {
      page_type: 'basket',
      shop_product_sku: this.subscriptionTemplateId,
      shop_product_status: 'view',
      shop_product_name: this.product.texts.productName,
    });

    this.hookService = productDetailsService(response, this);

    this.hookService.proxy('productGroupAction', () => {
      if (this.product.productGroup && this.product.productGroupMembers && this.product.productGroupMembers.length) {
        this.isProductGroup = true;
        let index = this.product.productGroupMembers.findIndex(p => p.subscriptionTemplateId === this.subscriptionTemplateId);
        if (index === -1) index = 0;
        this.overrideWithProductGroup(index);
      }
    });
  },

  methods: {
    overrideWithProductGroup(index) {
      const m = this.product.productGroupMembers[index];
      this.selectedSubscriptionTemplateId = m.subscriptionTemplateId;
      this.product.texts.regularPriceOverrideText = m.regularPriceOverrideText;
      this.product.texts.promotionPriceOverrideText = m.promotionPriceOverrideText;
      this.product.texts.minimumCommitmentPeriodText = m.minimumCommitmentPeriodText;
      this.product.texts.minimumCommitmentPeriodOverrideText = m.minimumCommitmentPeriodOverrideText;
      this.product.price = m.price;
      this.product.bookingAllowed = m.bookingAllowed;
      this.product.reservedInstances = m.reservedInstances;
      this.product.canNotSucceed = m.canNotSucceed;
      this.product.currentInstances = m.currentInstances;
      this.product.maxInstances = m.maxInstances;
      this.product.excludedBy = m.excludedBy;
      this.product.allowedProductChangeFrom = m.allowedProductChangeFrom;
    },

    onNext() {
      this.hookService.proxy('nextAction', () => {
        this.$router.push({
          name: PRODUCT_ORDER_SELECTION,
          params: { originalSubscriptionTemplateId: this.optionalSubscriptionTemplateId, subscriptionTemplateId: this.selectedSubscriptionTemplateId },
        });
      });
    },
  },
};
</script>

<style scoped>
fieldset legend {
  padding-top: 24px;
}
fieldset {
  padding-bottom: 12px;
}

.button-bar > span {
  position: relative;
  top: -4px;
  display: inline-block;
}
</style>
